<template>
    <span class="uk-display-inline-block">
        <Button
            v-if="!isImport"
            isRound
            :action="actionExport"
            :is-disabled="!articles || articles.length === 0"
            :tooltip="$t('csv.export')"
            has-action
            mode="default">
            <Icon icon="download" />
        </Button>

        <span v-else>
            <span class="uk-hidden" uk-form-custom>
                <input
                    id="articles-csv"
                    ref="csv"
                    accept=".csv,text/csv"
                    type="file"
                    v-on:change="actionImport()"
                    v-on:click="$refs.csv.value = null" />
            </span>

            <Button
                isRound
                :action="triggerInputDialog"
                :is-loading="loading"
                :tooltip="$t('csv.import')"
                has-action
                mode="default">
                <Icon icon="upload" />
            </Button>

            <Modal :id="modalIdResult" :title="$t('csv.import')">
                <InfoBox :mode="errors.length > 0 ? 'warning' : 'success'">
                    <div v-if="result">
                        {{ result }}
                    </div>

                    <div v-for="error in errors" :key="error">
                        {{ error }}
                    </div>
                </InfoBox>

                <div class="uk-flex uk-flex-center uk-margin-medium-top">
                    <Button
                        :text="$t('close')"
                        class="uk-modal-close"
                        is-large
                        is-max
                        is-min-width
                        mode="default" />
                </div>
            </Modal>
        </span>
    </span>
</template>

<script>
import axios from 'axios';
import UIkit from 'uikit';
import Button from '@/components/generic/Button.vue';
import Icon from '@/components/generic/Icon';
import InfoBox from '@/components/generic/InfoBox';
import Modal from '@/components/generic/Modal';

const delimiter = ';';
const byteOrderMark = '\uFEFF';

export default {
    name: 'ArticlesCsvForm',
    components: {Button, Icon, InfoBox, Modal},
    props: {
        articles: Array,
        name: String,
        isImport: Boolean,
    },
    data() {
        return {
            modalIdResult: 'articles-csv-result',

            loading: false,
            result: null,
            errors: [],
        };
    },
    methods: {
        triggerInputDialog() {
            document.getElementById('articles-csv').click();
        },
        actionExport() {
            const columns = this.columns();

            let rows = [columns.map(this.escape).join(delimiter)];
            for (let i = 0, j = this.articles.length; i < j; i++) {
                const article = this.articles[i];

                rows.push(
                    columns
                        .map((column) =>
                            this.escape(this.stringify(article[column]))
                        )
                        .join(delimiter)
                );
            }

            const blob = new Blob([byteOrderMark + rows.join('\r\n')], {
                type: 'text/csv;charset=utf-8;',
            });

            let link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = this.fileName();
            link.click();

            window.URL.revokeObjectURL(link.href);
        },
        columns() {
            let columns = [];

            for (let i = 0, j = this.articles.length; i < j; i++) {
                const keys = Object.keys(this.articles[i]);

                for (let k = 0, l = keys.length; k < l; k++) {
                    if (!columns.includes(keys[k])) {
                        columns.push(keys[k]);
                    }
                }
            }

            return columns;
        },
        fileName() {
            const name = this.name ? this.name + ' - ' : '';
            const date = new Date().toISOString().slice(0, 10);

            return (
                name +
                this.$t('articles') +
                ' ' +
                date +
                '.csv'
            ).replace(/[\\/:*?"<>|]/g, '-');
        },
        stringify(value) {
            if (value === null || value === undefined) {
                return '';
            }

            if (typeof value === 'string') {
                return value;
            }

            return JSON.stringify(value);
        },
        parseValue(value, column) {
            if (value === '') {
                return null;
            }

            if (this.columnType(column) === 'string') {
                return value;
            }

            try {
                return JSON.parse(value);
            } catch (e) {
                return value;
            }
        },
        columnType(column) {
            for (let i = 0, j = this.articles.length; i < j; i++) {
                const value = this.articles[i][column];

                if (value !== null && value !== undefined) {
                    return typeof value;
                }
            }

            return null;
        },
        escape(value) {
            if (
                value.includes(delimiter) ||
                value.includes('"') ||
                value.includes('\n')
            ) {
                return '"' + value.split('"').join('""') + '"';
            }

            return value;
        },
        actionImport() {
            const file = this.$refs.csv.files[0];
            if (!file) {
                return;
            }

            this.loading = true;
            this.result = null;
            this.errors = [];

            let reader = new FileReader();
            reader.onload = () => {
                this.importRows(this.parse(reader.result));
            };
            reader.onerror = () => {
                this.errors = [this.$t('client.error.message')];
                this.finish();
            };
            reader.readAsText(file);
        },
        parse(text) {
            let rows = [];
            let row = [];
            let field = '';
            let quoted = false;

            if (text.startsWith(byteOrderMark)) {
                text = text.slice(1);
            }

            for (let i = 0, j = text.length; i < j; i++) {
                const char = text[i];

                if (quoted) {
                    if (char !== '"') {
                        field += char;
                        continue;
                    }

                    if (text[i + 1] === '"') {
                        field += '"';
                        i++;
                        continue;
                    }

                    quoted = false;
                    continue;
                }

                if (char === '"') {
                    quoted = true;
                    continue;
                }

                if (char === delimiter) {
                    row.push(field);
                    field = '';
                    continue;
                }

                if (char === '\r') {
                    continue;
                }

                if (char === '\n') {
                    row.push(field);
                    rows.push(row);
                    row = [];
                    field = '';
                    continue;
                }

                field += char;
            }

            if (field !== '' || row.length > 0) {
                row.push(field);
                rows.push(row);
            }

            return rows.filter((fields) =>
                fields.some((field) => field.trim() !== '')
            );
        },
        async importRows(rows) {
            if (rows.length < 2) {
                this.errors = [this.$t('client.error.message')];
                this.finish();
                return;
            }

            const header = rows[0].map((column) => column.trim());

            let imported = 0;
            for (let i = 1, j = rows.length; i < j; i++) {
                try {
                    await this.importRow(header, rows[i]);
                    imported++;
                } catch (e) {
                    this.checkGenericError(e);
                    this.errors.push(
                        this.$t('csv.import.error', {
                            x: i + 1,
                            y: this.errorText(e),
                        })
                    );
                }
            }

            this.result = this.$t('csv.import.result', {
                x: imported,
                y: rows.length - 1,
            });
            this.finish();

            if (imported > 0) {
                this.$emit('updated');
            }
        },
        finish() {
            this.loading = false;

            UIkit.modal('#' + this.modalIdResult).show();
        },
        importRow(header, row) {
            let article = {};
            for (let i = 0, j = header.length; i < j; i++) {
                article[header[i]] = this.parseValue(
                    row[i] === undefined ? '' : row[i].trim(),
                    header[i]
                );
            }

            let http = axios.create();

            if (
                article.id &&
                this.articles.some((existing) => existing.id === article.id)
            ) {
                return http.put(
                    this.apiUrl + '/articles/' + article.id,
                    article
                );
            }

            return http.post(this.apiUrl + '/articles', article);
        },
        errorText(e) {
            if (!e.response || !e.response.data) {
                return this.$t('no.connection.message');
            }

            const data = e.response.data;
            const message = data.error ? data.error : data.response;

            if (typeof message === 'string') {
                return message;
            }

            return Object.values(message ? message : {}).join(', ');
        },
    },
};
</script>

<style lang="scss" scoped></style>
